import { getEntityImageSrc } from "./uploadImage";

/**
 * ticketNormalizer.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Frontend compatibility layer for the universal ticket system.
 *
 * The ticket_collection contains three possible ticket shapes:
 *
 *   Shape A — Legacy (pre-migration)
 *     objective.{pc, printer, software, smb, room, problem}
 *     desc, importance, comment[]
 *     No: domain, title, priority, relatedObjects, description, comments
 *
 *   Shape B — Migrated (migration script applied)
 *     All of Shape A's fields still present inside `legacy.*`
 *     Plus: title, domain, priority, description, relatedObjects[],
 *           comments[], problem (top-level), migrationMeta
 *
 *   Shape C — Native universal (created after the new system went live)
 *     title, domain, priority, type, description, relatedObjects[]
 *     problem (top-level), problemGroup, comments[]
 *     objective.* may be present for IT-domain tickets
 *     No `legacy.*`
 *
 * The backend normalizer (normalizeTicket.js) already aliases many fields
 * before they leave the API, so Shape A → Shape B in transit.
 * This frontend normalizer handles whatever arrives and guarantees a single
 * stable interface for all React components to consume.
 *
 * Rule: READ from this normalizer. WRITE using the raw field names so the
 *       server stores the canonical values.
 */

// ─── Legacy entity map ────────────────────────────────────────────────────────

const LEGACY_ENTITY_MAP = {
	pc: "pc",
	printer: "printer",
	software: "software",
	smb: "smb",
	room: "room",
	contact: "contact",
	productBuying: "productBuying",
	productSelling: "productSelling"
};

// ─── Low-level helpers ────────────────────────────────────────────────────────

const OBJECT_ID_HEX = /^[a-fA-F0-9]{24}$/;

/**
 * Normalize a legacy objective ref (PC, room, …) to a Mongo ObjectId string, or null.
 * Empty objects `{}` from the form are treated as unset (they are truthy in JS and must not become entityId: {}).
 */
export function normalizeObjectiveRefForSave(val) {
	if (val == null || val === "") return null;
	if (typeof val === "string") return OBJECT_ID_HEX.test(val) ? val : null;
	if (typeof val === "object" && !Array.isArray(val)) {
		if (Object.keys(val).length === 0) return null;
		const id = val._id;
		if (id != null && OBJECT_ID_HEX.test(String(id))) return String(id);
		return null;
	}
	return null;
}

/** Drop invalid / empty legacy refs so Mongoose never receives `{}` for ObjectId fields. */
export function sanitizeObjectiveForTicketSave(objective) {
	if (!objective) return {};
	const o = { ...objective };
	for (const key of Object.keys(LEGACY_ENTITY_MAP)) {
		if (!(key in o)) continue;
		const n = normalizeObjectiveRefForSave(o[key]);
		if (n == null) delete o[key];
		else o[key] = n;
	}
	const pr = o.problem;
	if (pr != null) {
		if (typeof pr === "object" && pr._id != null) {
			const id = String(pr._id);
			if (OBJECT_ID_HEX.test(id)) o.problem = id;
			else delete o.problem;
		} else if (typeof pr === "string" && OBJECT_ID_HEX.test(pr)) {
			o.problem = pr;
		} else {
			delete o.problem;
		}
	}
	return o;
}

/** Convert `objective.*` fields → `relatedObjects[]` with valid `entityId` strings only. */
export function buildRelatedObjectsFromLegacyObjective(objective) {
	if (!objective) return [];
	return Object.entries(LEGACY_ENTITY_MAP)
		.map(([entityType, key]) => {
			const val = objective[key];
			const entityId = normalizeObjectiveRefForSave(val);
			if (!entityId) return null;
			const resolved =
				typeof val === "object" && val !== null && val._id != null ? val : undefined;
			return {
				entityType,
				entityId,
				label: key.charAt(0).toUpperCase() + key.slice(1),
				...(resolved ? { _resolved: resolved } : {})
			};
		})
		.filter(Boolean);
}

/** Best-effort domain for tickets with no `domain` field. */
export function inferDomainFromLegacyTicket(ticket) {
	if (ticket?.domain) return ticket.domain;
	const obj = ticket?.objective || {};
	if (obj.pc || obj.printer || obj.software || obj.smb || obj.room || obj.problem) return "it";
	return "other";
}

/**
 * Human-readable title.
 * Priority: stored title → migrated problem name → legacy objective.problem name → fallback.
 */
export function inferTitleFromLegacyTicket(ticket, lang = "en") {
	if (ticket?.title) return ticket.title;

	// Shape B / C: problem might be a top-level populated doc
	const topLevelProblemName = ticket?.problem?.name?.[lang] || ticket?.problem?.name?.en;
	if (topLevelProblemName) {
		const ctx = [ticket?.objective?.room?.name, ticket?.objective?.pc?.name]
			.filter(Boolean)
			.join(" / ");
		return ctx ? `${topLevelProblemName} — ${ctx}` : topLevelProblemName;
	}

	// Shape A: problem is inside objective
	const objProblemName =
		ticket?.objective?.problem?.name?.[lang] || ticket?.objective?.problem?.name?.en;
	if (objProblemName) {
		const ctx = [ticket?.objective?.room?.name, ticket?.objective?.pc?.name]
			.filter(Boolean)
			.join(" / ");
		return ctx ? `${objProblemName} — ${ctx}` : objProblemName;
	}

	return `Ticket #${ticket?.ID || ticket?._id || "?"}`;
}

/** `importance` → `priority` mapping for legacy tickets. */
export function mapLegacyImportanceToPriority(importance) {
	const map = { low: "low", medium: "medium", high: "high" };
	return map[(importance || "").toLowerCase()] || "medium";
}

/** Effective priority regardless of which field stores it. */
export function getEffectivePriority(ticket) {
	return ticket?.priority || mapLegacyImportanceToPriority(ticket?.importance);
}

/**
 * Canonical problem reference — Shape C/B stores it top-level;
 * Shape A stores it inside objective.
 */
export function getEffectiveProblem(ticket) {
	return ticket?.problem || ticket?.objective?.problem || null;
}

/**
 * Canonical description — Shape B/C uses `description`; Shape A uses `desc`.
 * The backend normalizer already aliases both, but we defend here too.
 */
export function getEffectiveDescription(ticket) {
	return ticket?.description || ticket?.desc || "";
}

/**
 * Canonical comment array.
 * Shape B/C uses `comments` (normalized); Shape A uses `comment` (raw).
 * The backend normalizer exposes both, so we prefer `comments` but fall back.
 */
export function getEffectiveComments(ticket) {
	if (Array.isArray(ticket?.comments) && ticket.comments.length > 0) return ticket.comments;
	if (Array.isArray(ticket?.comment) && ticket.comment.length > 0) return ticket.comment;
	return [];
}

/**
 * Canonical relatedObjects array.
 * Shape B/C has `relatedObjects[]`; Shape A only has `objective.*`.
 */
export function getEffectiveRelatedObjects(ticket) {
	if (Array.isArray(ticket?.relatedObjects) && ticket.relatedObjects.length > 0) {
		return ticket.relatedObjects;
	}
	return buildRelatedObjectsFromLegacyObjective(ticket?.objective);
}

// ─── Primary normalizer ───────────────────────────────────────────────────────

/**
 * Normalise any ticket into the unified shape the UI consumes.
 * Does NOT mutate the input.
 */
export function normalizeTicketForUI(ticket, lang = "en") {
	if (!ticket) return null;

	const isLegacy = !ticket.domain && !ticket.relatedObjects?.length && !ticket.migrationMeta;

	return {
		// Spread everything first (keeps legacy.*, migrationMeta, all raw fields)
		...ticket,

		// ── Computed / aliased fields ──────────────────────────────────────────
		_isLegacy: isLegacy,
		_normalized: true,
		title: inferTitleFromLegacyTicket(ticket, lang),
		domain: inferDomainFromLegacyTicket(ticket),
		priority: getEffectivePriority(ticket),
		description: getEffectiveDescription(ticket),
		desc: getEffectiveDescription(ticket), // keep `desc` alias for existing code
		problem: getEffectiveProblem(ticket),
		relatedObjects: getEffectiveRelatedObjects(ticket),
		comments: getEffectiveComments(ticket),
		comment: getEffectiveComments(ticket) // keep `comment` alias for existing code
	};
}

/** Normalise an array (list page). */
export function normalizeTicketList(tickets, lang = "en") {
	if (!Array.isArray(tickets)) return [];
	return tickets.map((t) => normalizeTicketForUI(t, lang));
}

// ─── Build helpers (used on SAVE, not display) ────────────────────────────────

/**
 * Build `relatedObjects[]` for saving a new ticket from IT-style objective fields.
 * The backend stores this in `relatedObjects` alongside the legacy `objective`.
 */
/**
 * @param {object} objective - legacy objective.{pc, printer, …}
 * @param {{ relatedListId: string, manualKey: string }[]} manualRelated - manual list picks
 */
export function buildRelatedObjectsForSave(objective, manualRelated = []) {
	const base = buildRelatedObjectsFromLegacyObjective(objective);
	const extras = (manualRelated || [])
		.filter((m) => m.relatedListId && m.manualKey)
		.map((m) => ({
			entityType: "manual",
			relatedListId: m.relatedListId,
			manualKey: m.manualKey
		}));
	// Only schema fields — no `label` / `_resolved` / empty entityId (breaks Mongoose casting).
	return [...base, ...extras].map((row) => {
		if (row.entityType === "manual") {
			return {
				entityType: "manual",
				relatedListId: row.relatedListId,
				manualKey: row.manualKey
			};
		}
		return {
			entityType: row.entityType,
			entityId: row.entityId
		};
	});
}

// ─── Constants for form selectors ────────────────────────────────────────────

export const PRIORITY_OPTIONS = [
	{ _id: "low", name: { en: "Low", ru: "Низкий", hy: "Ցածր" } },
	{ _id: "medium", name: { en: "Medium", ru: "Средний", hy: "Միջին" } },
	{ _id: "high", name: { en: "High", ru: "Высокий", hy: "Բարձր" } },
	{ _id: "critical", name: { en: "Critical", ru: "Критический", hy: "Կրիտիկական" } }
];

export const TICKET_TYPE_OPTIONS = [
	{ _id: "incident", name: { en: "Incident", ru: "Инцидент", hy: "Միջադեպ" } },
	{ _id: "request", name: { en: "Request", ru: "Запрос", hy: "Հայց" } },
	{ _id: "problem", name: { en: "Problem", ru: "Проблема", hy: "Խնդիր" } },
	{ _id: "task", name: { en: "Task", ru: "Задача", hy: "Առաջադրանք" } },
	{ _id: "question", name: { en: "Question", ru: "Вопрос", hy: "Հարց" } }
];

/**
 * Domain keys a ticket type is limited to. Empty = available in every domain.
 * If `domains` is a saved array (including []), it wins; otherwise uses legacy `domain`.
 */
export function ticketTypeDomainKeys(tt) {
	if (!tt) return [];
	if (Array.isArray(tt.domains)) {
		const cleaned = tt.domains.map((x) => String(x ?? "").trim()).filter(Boolean);
		if (cleaned.length) return [...new Set(cleaned)];
		return [];
	}
	const one = tt.domain && String(tt.domain).trim();
	return one ? [one] : [];
}

/** Whether ticket type `tt` applies when creating/filtering in `domainKey` (empty domainKey = no filter). */
export function ticketTypeAppliesToDomain(tt, domainKey) {
	const keys = ticketTypeDomainKeys(tt);
	if (!keys.length) return true;
	const d = String(domainKey ?? "").trim();
	return d ? keys.includes(d) : true;
}

/** Latest active `destiny: image` upload on an entity, as a full optimized preview URL. */
function menuImageUrlFromUploads(entity) {
	const src = getEntityImageSrc(entity, { targetWidth: 56 });
	return src || null;
}

/** Latest active `destiny: image` upload on a ticket type, as a full optimized preview URL. */
export function ticketTypeMenuImageUrl(tt) {
	return menuImageUrlFromUploads(tt);
}

/** Same as ticket types — menu icon for a ticket domain row. */
export function ticketDomainMenuImageUrl(d) {
	return menuImageUrlFromUploads(d);
}

/** Mantine Badge color + `T()` key per `ticket_domain.key` / ticket.domain slug. */
export const DOMAIN_CONFIG = {
	it: { key: "domainIT", color: "blue" },
	hr: { key: "domainHR", color: "grape" },
	finance: { key: "domainFinance", color: "teal" },
	facility: { key: "domainFacility", color: "orange" },
	security: { key: "domainSecurity", color: "red" },
	other: { key: "domainOther", color: "gray" }
};

/**
 * Maps `ticket_related_list.entityType` → legacy `objective.*` field and ticket permission.
 * New entity types need a row here (and loaded options on the ticket page) to appear.
 */
export const TICKET_ENTITY_FORM_REGISTRY = {
	// IT
	pc:              { objectiveKey: "pc",              permissionModel: "pc",              syncRoomFromPc: true },
	monitor:         { objectiveKey: "monitor",         permissionModel: "monitor" },
	printer:         { objectiveKey: "printer",         permissionModel: "printer" },
	keyboard:        { objectiveKey: "keyboard",        permissionModel: "keyboard" },
	mouse:           { objectiveKey: "mouse",           permissionModel: "mouse" },
	software:        { objectiveKey: "software",        permissionModel: "software" },
	smb:             { objectiveKey: "smb",             permissionModel: "smb" },
	room:            { objectiveKey: "room",            permissionModel: "room" },
	router:          { objectiveKey: "router",          permissionModel: "router" },
	switch:          { objectiveKey: "switch",          permissionModel: "switch" },
	cpu:             { objectiveKey: "cpu",             permissionModel: "cpu" },
	gpu:             { objectiveKey: "gpu",             permissionModel: "gpu" },
	ram:             { objectiveKey: "ram",             permissionModel: "ram" },
	rom:             { objectiveKey: "rom",             permissionModel: "rom" },
	warrantyProvider:{ objectiveKey: "warrantyProvider",permissionModel: "warrantyProvider" },
	// HR
	user:            { assignedToUser: true,            permissionModel: "assignedToUser" },
	department:      { objectiveKey: "department",      permissionModel: "department" },
	position:        { objectiveKey: "position",        permissionModel: "position" },
	payroll:         { objectiveKey: "payroll",         permissionModel: "payroll" },
	// CRM / E-commerce. Buying and selling intentionally use distinct entity
	// types so a problem can request both product selectors independently.
	contact:         { objectiveKey: "contact",         permissionModel: "relatedObjects" },
	productBuying:   { objectiveKey: "productBuying",   permissionModel: "relatedObjects" },
	productSelling:  { objectiveKey: "productSelling",  permissionModel: "relatedObjects" },
};

const ENTITY_FALLBACK_T_KEYS = {
	pc:              "pcs",
	monitor:         "monitor",
	printer:         "printer",
	keyboard:        "keyboard",
	mouse:           "mouse",
	software:        "software",
	smb:             "smb",
	room:            "room",
	router:          "router",
	switch:          "switch",
	cpu:             "cpu",
	gpu:             "gpu",
	ram:             "ram",
	rom:             "rom",
	warrantyProvider:"warrantyProvider",
	user:            "assignedToUser",
	department:      "department",
	position:        "position",
	payroll:         "payroll",
	contact:         "contacts",
	productBuying:   "productsForBuying",
	productSelling:  "productsForSelling",
};

/** Label from related-list `name` in DB, or translator fallback. */
export function labelForTicketEntitySpec(spec, lang, T) {
	if (spec?.label && (spec.label[lang] || spec.label.en || spec.label.ru || spec.label.hy)) {
		const n = spec.label;
		return n[lang] || n.en || n.ru || n.hy || spec.entityType;
	}
	const key = ENTITY_FALLBACK_T_KEYS[spec.entityType];
	return key ? T(key, lang) : spec.entityType;
}

function pushEntitySpec(entitySpecs, seen, entityType, labelDoc) {
	if (!entityType || seen.has(entityType)) return;
	seen.add(entityType);
	const label =
		labelDoc && (labelDoc.en || labelDoc.ru || labelDoc.hy) ? { ...labelDoc } : null;
	entitySpecs.push({ entityType, label });
}

/**
 * From a problem document (with optional populated `relatedLists`), derive which
 * entity pickers and manual lists the ticket form should show.
 * `entitySpecs` order follows `relatedLists`; labels come from each list definition when present.
 */
export function resolveRelatedSpecsFromProblem(problem) {
	if (!problem) {
		return { entitySpecs: [], manualLists: [] };
	}
	const lists = problem.relatedLists;
	if (Array.isArray(lists) && lists.length > 0) {
		const entitySpecs = [];
		const seen = new Set();
		const manualLists = [];
		for (const L of lists) {
			if (typeof L !== "object" || L === null) continue;
			if (L.isActive === false) continue;
			const id = L._id != null ? String(L._id) : null;
			const st = L.sourceType;

			if (st === "manual") {
				if (id) {
					manualLists.push({
						id,
						items: (L.manualItems || []).filter((m) => m && m.key)
					});
				}
			} else if (st === "smb") {
				if (id) manualLists.push({ id, items: [], live: true });
			} else if (st === "entity") {
				pushEntitySpec(entitySpecs, seen, L.entityType, L.name);
			} else {
				// Populated doc without sourceType (older rows) — infer from fields
				const hasManual =
					Array.isArray(L.manualItems) &&
					L.manualItems.some((m) => m && String(m.key || "").trim());
				if (hasManual && id) {
					manualLists.push({
						id,
						items: (L.manualItems || []).filter((m) => m && m.key)
					});
				} else if (L.entityType) {
					pushEntitySpec(entitySpecs, seen, L.entityType, L.name);
				}
			}
		}
		if (entitySpecs.length > 0 || manualLists.length > 0) {
			return { entitySpecs, manualLists };
		}
		// relatedLists held only unresolved refs or empty docs — use legacy fields
	}
	const fromArr = problem.relatedEntityTypes?.length
		? problem.relatedEntityTypes
		: problem.relatedList
			? [problem.relatedList]
			: [];
	const entitySpecs = fromArr.map((entityType) => ({ entityType, label: null }));
	return { entitySpecs, manualLists: [] };
}

/**
 * Ordered steps for progressive ticket create UI: one manual list or one entity picker at a time,
 * in the same order as `problem.relatedLists` (with entity dedupe like `resolveRelatedSpecsFromProblem`).
 */
export function resolveOrderedRelatedStepsFromProblem(problem) {
	if (!problem) return [];
	const lists = problem.relatedLists;
	const steps = [];
	const seenEntity = new Set();

	const addManual = (listId, manualItems, nameDoc) => {
		const items = (manualItems || []).filter((m) => m && m.key);
		if (!listId || !items.length) return;
		steps.push({
			type: "manual",
			id: String(listId),
			items,
			label: nameDoc,
		});
	};
	const addEntity = (entityType, nameDoc) => {
		if (!entityType || seenEntity.has(entityType)) return;
		seenEntity.add(entityType);
		steps.push({
			type: "entity",
			entityType,
			label:
				nameDoc && (nameDoc.en || nameDoc.ru || nameDoc.hy) ? { ...nameDoc } : null,
		});
	};

	if (Array.isArray(lists) && lists.length > 0) {
		for (const L of lists) {
			if (typeof L !== "object" || L === null) continue;
			if (L.isActive === false) continue;
			const id = L._id != null ? String(L._id) : null;
			const st = L.sourceType;

			if (st === "manual") {
				addManual(id, L.manualItems, L.name);
			} else if (st === "smb") {
				if (id) steps.push({ type: "manual", id, items: [], live: true, label: L.name });
			} else if (st === "entity") {
				addEntity(L.entityType, L.name);
			} else {
				const hasManual =
					Array.isArray(L.manualItems) &&
					L.manualItems.some((m) => m && String(m.key || "").trim());
				if (hasManual && id) addManual(id, L.manualItems, L.name);
				else if (L.entityType) addEntity(L.entityType, L.name);
			}
		}
		if (steps.length) return steps;
	}

	const fromArr = problem.relatedEntityTypes?.length
		? problem.relatedEntityTypes
		: problem.relatedList
			? [problem.relatedList]
			: [];
	for (const entityType of fromArr) {
		addEntity(entityType, null);
	}
	return steps;
}
